// frontend/src/pages/RegisterPage.jsx
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import PropTypes from "prop-types";
import { useAuth } from "../context/AuthContext";
import { register as registerApi } from "../api/auth";
import PasswordStrengthMeter from "../components/PasswordStrengthMeter";

/**
 * FieldError — inline validation message.
 */
function FieldError({ message }) {
  if (!message) return null;
  return <p className="mt-1 text-xs text-red-600">{message}</p>;
}
FieldError.propTypes = { message: PropTypes.string };
FieldError.defaultProps = { message: "" };

/**
 * RegisterPage — create a new admin account and redirect to /codes.
 *
 * Validation (mirrors the server policy):
 *   - Email must be valid format
 *   - Password 10–72 chars with at least one digit or special character
 *   - Confirm password must match
 *   - A real-time strength meter updates as the user types
 *
 * On success: stores returned token via AuthContext, redirects to /codes.
 * On 409:     shows inline "email already registered" error.
 */
function RegisterPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors },
  } = useForm({ mode: "onTouched" });

  const password = watch("password");

  const onSubmit = async ({ email, password: pw }) => {
    setSubmitting(true);
    try {
      // Register returns a token — use it to log straight in
      await registerApi(email, pw);
      await login(email, pw);
      toast.success("Account created. Welcome to QRFlow!");
      navigate("/codes", { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) {
        setError("email", { message: "This email is already registered." });
      } else {
        toast.error("Registration failed. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-[70vh] items-center justify-center bg-gray-50 px-4 py-12">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-md p-8">
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold text-gray-900">QRFlow</h1>
          <p className="mt-1 text-sm text-gray-500">Create your account</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-5">
          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email address
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              className={`w-full rounded-lg border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.email ? "border-red-400" : "border-gray-300"
              }`}
              {...register("email", {
                required: "Email is required.",
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: "Please enter a valid email address.",
                },
              })}
            />
            <FieldError message={errors.email?.message} />
          </div>

          {/* Password */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <input
              id="password"
              type="password"
              autoComplete="new-password"
              className={`w-full rounded-lg border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.password ? "border-red-400" : "border-gray-300"
              }`}
              {...register("password", {
                required: "Password is required.",
                minLength: {
                  value: 10,
                  message: "Password must be at least 10 characters.",
                },
                maxLength: { value: 72, message: "Password must be 72 characters or fewer." },
                validate: (val) =>
                  /[^A-Za-z]/.test(val) ||
                  "Password must contain at least one digit or special character.",
              })}
            />
            <FieldError message={errors.password?.message} />
            <PasswordStrengthMeter password={password} />
          </div>

          {/* Confirm password */}
          <div>
            <label htmlFor="confirm" className="block text-sm font-medium text-gray-700 mb-1">
              Confirm password
            </label>
            <input
              id="confirm"
              type="password"
              autoComplete="new-password"
              className={`w-full rounded-lg border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.confirm ? "border-red-400" : "border-gray-300"
              }`}
              {...register("confirm", {
                required: "Please confirm your password.",
                validate: (val) => val === password || "Passwords do not match.",
              })}
            />
            <FieldError message={errors.confirm?.message} />
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            {submitting ? "Creating account…" : "Create account"}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-gray-500">
          Already have an account?{" "}
          <Link to="/login" className="text-blue-600 hover:underline font-medium">
            Sign in
          </Link>
        </p>
        <p className="mt-2 text-center text-xs text-gray-400">
          <Link to="/legal/privacy" className="hover:underline">
            Privacy Policy
          </Link>
        </p>
      </div>
    </div>
  );
}

export default RegisterPage;
